import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { generateUserId } from '../../utils/profileHelpers';
import { logError, logInfo } from '../../utils/logger';

// Sign in with email/password
export const signIn = async (email, password) => {
  try {
    // Validate inputs
    if (!email?.trim()) {
      return { 
        isValid: false,
        message: 'Email is required',
        style: 'border-red-500 focus:ring-red-500'
      };
    }
    if (!password?.trim()) {
      return {
        isValid: false,
        message: 'Password is required', 
        style: 'border-red-500 focus:ring-red-500'
      };
    }

    // For sign in, we don't need to check if email exists first
    const result = await signInWithEmailAndPassword(auth, email, password);
    if (!result.user) {
      throw new Error('No user data returned from authentication');
    }
    
    // Check if user profile exists
    const userRef = doc(db, 'userProfiles', result.user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      // Create basic profile for new users
      await setDoc(userRef, {
        userId: result.user.uid,
        email: result.user.email.toLowerCase(),
        emailVerified: result.user.emailVerified,
        name: result.user.displayName || result.user.email.split('@')[0],
        role: email === 'admin@oversizehub.net' ? 'admin' : 'viewer',
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      logInfo('Created new user profile');
    }

    // Update last login
    await setDoc(userRef, {
      lastLogin: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }, { merge: true });

    logInfo('User signed in successfully');
    return result;
  } catch (error) {
    const errorMessage = (() => {
      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/invalid-email':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          return 'Invalid email or password';
        case 'auth/user-disabled':
          return 'This account has been disabled';
        case 'auth/too-many-requests':
          return 'Too many failed attempts. Please try again later';
        case 'auth/network-request-failed':
          return 'Network error. Please check your connection';
        default:
          return 'An error occurred during sign in';
      }
    })();

    logError('Sign in failed:', { code: error.code, message: errorMessage });
    return {
      isValid: false,
      message: errorMessage,
      style: 'border-red-500 focus:ring-red-500'
    };
  }
};

export const createAccount = async (email, password) => {
  try {
    console.log('[AuthService] Starting account creation...');
    
    // Get stored form data and verification status
    const pendingEmail = localStorage.getItem('pendingEmail');
    const isVerified = localStorage.getItem('emailVerified');
    const formDataStr = localStorage.getItem('signupFormData');
    
    if (!pendingEmail || !isVerified) {
      logError('Email verification required');
      throw new Error('Email must be verified before creating account');
    }

    if (!formDataStr) {
      logError('Missing signup form data');
      throw new Error('Missing required account data');
    }

    const formData = JSON.parse(formDataStr);
    if (!formData.email || !formData.password || !formData.name || !formData.phone) {
      logError('Missing required fields:', formData);
      throw new Error('Missing required account fields');
    }

    // Create Firebase auth account
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // Create user profile
    const userProfile = {
      userId: generateUserId(),
      email: formData.email.toLowerCase(),
      emailVerified: true,
      name: formData.name,
      phone: formData.phone,
      role: 'viewer',
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await setDoc(doc(db, 'userProfiles', result.user.uid), userProfile);

    // Create user settings
    await setDoc(doc(db, 'userSettings', result.user.uid), {
      preferences: {
        language: 'en',
        notifications: { enabled: true, email: true },
        autoLogin: false,
        darkMode: false,
        units: 'metric'
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    // Clear verification data
    localStorage.removeItem('pendingEmail');
    localStorage.removeItem('emailVerified');
    localStorage.removeItem('verificationCode');
    localStorage.removeItem('signupFormData');

    logInfo('User account created successfully');
    return result.user;
  } catch (error) {
    const errorMessage = (() => {
      switch (error.code) {
        case 'auth/email-already-in-use':
          return 'An account with this email already exists';
        case 'auth/invalid-email':
          return 'Please enter a valid email address';
        case 'auth/operation-not-allowed':
          return 'Account creation is currently disabled';
        case 'auth/weak-password':
          return 'Password is too weak';
        default:
          return error.message || 'Failed to create account';
      }
    })();

    logError('Account creation failed:', { code: error.code, message: errorMessage });
    throw new Error(errorMessage);
  }
};

// Social sign in methods
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    
    // Create user profile if it doesn't exist
    const profileRef = doc(db, 'userProfiles', result.user.uid);
    const profile = await getDoc(profileRef);
    
    if (!profile.exists()) {
      await setDoc(profileRef, {
        userId: generateUserId(),
        email: result.user.email,
        emailVerified: result.user.emailVerified,
        name: result.user.displayName,
        profileImage: result.user.photoURL,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    
    logInfo('Google sign in successful');
    return result.user;
  } catch (error) {
    logError('Google sign in failed:', error);
    throw new Error('Google sign in failed');
  }
};

export const signInWithFacebook = async () => {
  try {
    const provider = new FacebookAuthProvider();
    const result = await signInWithPopup(auth, provider);
    
    // Create user profile if it doesn't exist
    const profileRef = doc(db, 'userProfiles', result.user.uid);
    const profile = await getDoc(profileRef);
    
    if (!profile.exists()) {
      await setDoc(profileRef, {
        userId: generateUserId(),
        email: result.user.email,
        emailVerified: result.user.emailVerified,
        name: result.user.displayName,
        profileImage: result.user.photoURL,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    
    logInfo('Facebook sign in successful');
    return result.user;
  } catch (error) {
    logError('Facebook sign in failed:', error);
    throw new Error('Facebook sign in failed');
  }
};
